// 現住地との比較行を、描き終わった詳細（SSR ページ / SPA ドロワー）に流し込む。
// SSR は localStorage を読めないので、マークアップは空の口だけを持って返り、
// このモジュールが読み込み後に埋める。SPA も SSR も同じ 1 つの経路を通る。
import { clearHome, homeLines, loadHome, metricValues, saveHome, type HomeSlot } from "../home";
import { t, type Lang } from "../i18n";
import { isMetricKey } from "../metrics";

type Track = (event: string, params: Record<string, string | number | boolean>) => void;

const WIRED = "homeWired";

/**
 * root の中の data-metric / data-value を持つ要素に、現住地との比較行を書き込む。
 * 現住地が無い（または今いる町が現住地の）場合は、行を隠したままにする。
 */
export function applyHome(root: ParentNode, storage: Storage, lang: Lang, onTrack?: Track): void {
  const bar = root.querySelector<HTMLElement>(".home-bar");
  const home = loadHome(storage);
  const slots = [...root.querySelectorAll<HTMLElement>("[data-metric][data-value]")];
  const currentCode = bar?.dataset.code;

  const byMetric = new Map<string, HTMLElement>();
  const wanted: HomeSlot[] = [];
  for (const el of slots) {
    const metric = el.dataset.metric ?? "";
    if (!isMetricKey(metric)) continue;
    byMetric.set(metric, el);
    wanted.push({ metric, value: numberOrNull(el.dataset.value) });
  }

  for (const el of byMetric.values()) hideLine(el);

  const lines = home === null ? [] : homeLines(home, wanted, lang, currentCode);
  for (const line of lines) {
    const target = byMetric.get(line.metric)?.querySelector<HTMLElement>(".tile-home");
    if (!target) continue;
    target.textContent = line.text;
    target.className = `tile-home tile-home--${line.delta}`;
    target.hidden = false;
  }

  if (bar) syncBar(bar, home === null ? null : home.name, home?.code === currentCode, lang);
  wire(root, bar, storage, lang, onTrack);
}

function hideLine(el: HTMLElement): void {
  const line = el.querySelector<HTMLElement>(".tile-home");
  if (!line) return;
  line.textContent = "";
  line.className = "tile-home";
  line.hidden = true;
}

/** バーの文言とボタンの出し分け。「現住地が無い / 別の町 / この町」の 3 状態しかない。 */
function syncBar(bar: HTMLElement, homeName: string | null, isThisTown: boolean, lang: Lang): void {
  const m = t(lang);
  const text = bar.querySelector<HTMLElement>(".home-bar-text");
  const setBtn = bar.querySelector<HTMLButtonElement>(".home-set");
  const clearBtn = bar.querySelector<HTMLButtonElement>(".home-clear");

  if (text) {
    text.textContent =
      homeName === null ? m.homeIntro : isThisTown ? m.homeIsThisTown : m.homeCurrentIs(homeName);
  }
  if (setBtn) setBtn.hidden = isThisTown;
  if (clearBtn) clearBtn.hidden = homeName === null;
  bar.classList.toggle("home-bar--set", homeName !== null);
}

function wire(
  root: ParentNode,
  bar: HTMLElement | null,
  storage: Storage,
  lang: Lang,
  onTrack?: Track,
): void {
  if (!bar || bar.dataset[WIRED] === "1") return;
  bar.dataset[WIRED] = "1";

  bar.querySelector<HTMLButtonElement>(".home-set")?.addEventListener("click", () => {
    const code = bar.dataset.code ?? "";
    if (!code) return;
    saveHome(storage, {
      code,
      name: bar.dataset.name ?? code,
      pref: bar.dataset.pref ?? "",
      builtAt: bar.dataset.builtAt ?? "",
      values: parseValues(bar.dataset.values),
    });
    applyHome(root, storage, lang, onTrack);
    onTrack?.("home_set", { code, pref: code.slice(0, 2) });
  });

  bar.querySelector<HTMLButtonElement>(".home-clear")?.addEventListener("click", () => {
    clearHome(storage);
    applyHome(root, storage, lang, onTrack);
    onTrack?.("home_clear", {});
  });
}

function numberOrNull(raw: string | undefined): number | null {
  if (raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** バーが持つ 12 指標の値。壊れていても 12 キーの表に揃えて返す（比較行が出ないだけ）。 */
function parseValues(raw: string | undefined) {
  try {
    const parsed: unknown = JSON.parse(raw ?? "{}");
    const o = typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
    return metricValues((key) => o[key]);
  } catch {
    return metricValues(() => null);
  }
}
